import React, {useEffect, useState} from 'react'
import {ImageCarouselModal} from './ImageCarouselModal'

const Carousel = (
  {
    loading,
    imagesArray,
    nomPiece,
    taille,
    indicator = 1,
    isVehic = false,
    index,
    displayMiniature = 4,
    width = 143,
    etatColorIndicator,
    isPOT = false
  }: {
    loading: boolean,
    imagesArray: any,
    nomPiece: string,
    taille: string,
    indicator?: number,
    isVehic?: boolean,
    index?: number,
    displayMiniature?: number,
    width?: number,
    etatColorIndicator?: string
    isPOT?: boolean
  }
) => {
  const [img, setImg] = useState<any>([])
  const [load, setLoad] = useState<boolean>(loading)
  const [activePhoto, setActivePhoto] = useState<number>(0)
  const [navigatePhoto, setNavigatePhoto] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const API_URL = process.env.REACT_APP_API_URL
  const imgPOT_url = `${API_URL}/photos/POT/`
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [errorImages, setErrorImages] = useState<number[]>([])

  // Repasse en "chargement" à chaque nouvelle liste d'images
  useEffect(() => {
    setIsLoading(true)
    setErrorImages([]) // Reset des erreurs sur une nouvelle liste
  }, [imagesArray])

  useEffect(() => {
    if (!imagesArray || imagesArray.length === 0) {
      setImg([])
      return
    }
    setImg(Array.from(new Set(imagesArray)))
    setLoad(true)

    const timer = setTimeout(() => {
      setActivePhoto(0)
      setNavigatePhoto(0)
      setLoad(false)
    }, 50)

    return () => clearTimeout(timer)
  }, [imagesArray])

  useEffect(() => {
    setActivePhoto(0)
    setNavigatePhoto(0)
  }, [index])

  useEffect(() => {
    if (isVehic && !imagesArray?.[0]?.startsWith('Photos')) setImg([])
  }, [isVehic, imagesArray])

  // Filtrer les images valides (en excluant celles en erreur) pour le rendu dynamique
  const validImages = img.filter((_, i) => !errorImages.includes(i))

  // Sécurise navigatePhoto et activePhoto si des images sont masquées
  useEffect(() => {
    const maxStart = Math.max(0, (validImages?.length ?? 0) - displayMiniature)
    if (navigatePhoto > maxStart) setNavigatePhoto(maxStart)
    if (activePhoto >= validImages.length && validImages.length > 0) {
      setActivePhoto(validImages.length - 1)
    }
  }, [validImages, displayMiniature, navigatePhoto, activePhoto])

  const getImageSrc = (image: string) => {
    if (!image || image?.toLowerCase() === 'null') return `https://placehold.co/${taille}`
    if (isVehic) return `https://d1zsawkzupb9kl.cloudfront.net/${image}`
    return `${isPOT ? imgPOT_url : 'https://koena.fr/photos/pieces/'}${image}`
  }

  const imageUrls: string[] = Array.from(
    new Set(imagesArray?.map(getImageSrc) ?? [])
  )

  // Gestionnaire d'erreur d'image
  const handleImageError = (originalIndex: number) => {
    setIsLoading(false)
    setErrorImages((prev) =>
      prev.includes(originalIndex) ? prev : [...prev, originalIndex]
    )
  }

  const renderImage = () => {
    const taillePhotos = taille.split('x')
    const hasValidImages = validImages.length > 0

    return (
      <div>
        {load || !hasValidImages ? (
          <div>
            <img src={`https://placehold.co/` + taille} className='w-100 rounded' alt='' />
          </div>
        ) : (
          <>
            <div className='position-relative'>
              {isLoading && (
                <div
                  className='position-absolute top-50 start-50 translate-middle'
                  style={{ zIndex: 1 }}
                >
                  <div className='spinner-border' role='status'>
                    <span className='visually-hidden'>Chargement...</span>
                  </div>
                </div>
              )}

              {/* Image principale */}
              {img?.map((image: string, i: number) => {
                if (errorImages.includes(i)) return null; // Masque l'image corrompue
                return (
                  <img
                    key={i}
                    src={getImageSrc(image)}
                    className={`img-fluid mb-3 rounded ${activePhoto !== i ? 'd-none' : ''}`}
                    alt={!isVehic ? nomPiece : ''}
                    style={{
                      width: "100%",
                      height: `${taillePhotos[1]}px`,
                      objectFit: "cover",
                    }}
                    onLoad={() => setIsLoading(false)}
                    onError={() => handleImageError(i)}
                    onClick={() => setSelectedIndex(i)}
                  />
                );
              })}

              {etatColorIndicator && (
                <span
                  className={`position-absolute bg-${etatColorIndicator} border border-light rounded-circle p-8`}
                  style={{ top: '15px', right: '15px' }}
                ></span>
              )}
            </div>

            {/* Miniatures */}
            <div
              className="position-relative d-flex overflow-hidden gap-2 p-1"
              style={{ whiteSpace: 'nowrap' }}
            >
              {(validImages?.length > displayMiniature) && (
                <div
                  className="position-absolute top-50 start-0 translate-middle-y ms-4"
                  style={{width: '40px', height: '40px', zIndex: 2}}
                >
                  <button
                    onClick={() => ((navigatePhoto - 1) >= 0) ? setNavigatePhoto(navigatePhoto - 1) : null}
                    disabled={!((navigatePhoto - 1) >= 0)}
                    className='btn btn-light rounded-circle d-flex align-items-center justify-content-center'
                    style={{width: '40px', height: '40px'}}
                  >
                    <i className='ms-1 bi bi-chevron-left'></i>
                  </button>
                </div>
              )}

              {img?.map((image: string, i: number) => {
                if (errorImages.includes(i)) return null; // Masque de la miniature si en erreur

                // Calcul de la visibilité basé sur les index valides
                const validIndex = validImages.indexOf(image);
                const isVisible = validIndex >= navigatePhoto && validIndex < (navigatePhoto + displayMiniature);

                return (
                  <img
                    key={i}
                    src={getImageSrc(image)}
                    className={`rounded border ${!isVisible ? 'd-none' : ''}`}
                    alt={!isVehic ? nomPiece : ''}
                    onClick={() => setActivePhoto(i)}
                    onError={() => handleImageError(i)}
                    style={{
                      width: `${width}px`,
                      height: "95px",
                      objectFit: "cover",
                      cursor: 'pointer',
                      borderColor: activePhoto === i ? '#0d6efd' : undefined // Optionnel : highlight de l'active
                    }}
                  />
                )
              })}

              {(validImages?.length > displayMiniature) && (
                <div
                  className="position-absolute top-50 end-0 translate-middle-y me-4"
                  style={{ width: '40px', height: '40px', zIndex: 2 }}
                >
                  <button
                    onClick={() => ((navigatePhoto + displayMiniature) < validImages?.length) ? setNavigatePhoto(navigatePhoto + 1) : null}
                    disabled={!((navigatePhoto + displayMiniature) < validImages?.length)}
                    className='btn btn-light rounded-circle d-flex align-items-center justify-content-center'
                    style={{ width: '40px', height: '40px' }}
                  >
                    <i className='ms-1 bi bi-chevron-right'></i>
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div>
      <div>{renderImage()}</div>
      {selectedIndex !== null && (
        <ImageCarouselModal
          images={imageUrls}
          initialIndex={selectedIndex}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </div>
  )
}

export default Carousel